import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFile,
  faFileArchive,
  faFileExcel,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFilePowerpoint,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";
import API from "../Api/API";

// Must match ALLOWED_ATTACHMENT_TYPES / MAX_ATTACHMENT_SIZE in ChatService.java
export const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
  "zip",
];
export const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
export const INLINE_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

export const getExtension = (name) => {
  const dot = (name || "").lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
};

export const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

export const fileIcon = (name) => {
  const ext = getExtension(name);
  if (ext === "pdf") return faFilePdf;
  if (["doc", "docx"].includes(ext)) return faFileWord;
  if (["xls", "xlsx", "csv"].includes(ext)) return faFileExcel;
  if (["ppt", "pptx"].includes(ext)) return faFilePowerpoint;
  if (ext === "zip") return faFileArchive;
  if (ext === "txt") return faFileLines;
  if ([...INLINE_IMAGE_EXTENSIONS, "heic"].includes(ext)) return faFileImage;
  return faFile;
};

// Attachments need the auth cookie, so they are fetched with axios and shown
// from blob URLs. Cached for the lifetime of the page.
const blobCache = new Map();

const loadBlobUrl = (messageId) => {
  if (!blobCache.has(messageId)) {
    const request = axios
      .get(API.url + "api/chat/attachments/" + messageId, {
        responseType: "blob",
        withCredentials: true,
      })
      .then((res) => URL.createObjectURL(res.data))
      .catch((err) => {
        blobCache.delete(messageId);
        throw err;
      });
    blobCache.set(messageId, request);
  }
  return blobCache.get(messageId);
};

const ChatAttachment = ({ messageId, attachment, onImageLoad }) => {
  const { t } = useTranslation(["chat"]);
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!attachment.image) return undefined;
    let cancelled = false;
    loadBlobUrl(messageId)
      .then((blobUrl) => !cancelled && setUrl(blobUrl))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [messageId, attachment.image]);

  const download = () => {
    setDownloading(true);
    loadBlobUrl(messageId)
      .then((blobUrl) => {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = attachment.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => setFailed(true))
      .finally(() => setDownloading(false));
  };

  if (attachment.image && !failed) {
    return (
      <button
        type="button"
        className="chat-image"
        onClick={() => url && window.open(url, "_blank", "noopener")}
        title={attachment.name}
        aria-label={`${t("open_image")}: ${attachment.name}`}
      >
        {url ? (
          <img src={url} alt={attachment.name} onLoad={onImageLoad} />
        ) : (
          <span className="chat-image-placeholder" />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      className="chat-file"
      onClick={download}
      disabled={downloading}
      title={t("download")}
    >
      <FontAwesomeIcon icon={fileIcon(attachment.name)} className="chat-file-icon" />
      <span className="chat-file-text">
        <span className="chat-file-name">{attachment.name}</span>
        <span className="chat-file-size">
          {failed ? t("attachment_unavailable") : formatSize(attachment.size)}
        </span>
      </span>
      <FontAwesomeIcon icon={faDownload} className="chat-file-download" />
    </button>
  );
};

export default ChatAttachment;
